import React, { useState, useEffect, useMemo } from "react";
import { EntityTablePage, DeleteConfirmationConfig } from "@/components/EntityTablePage";
import { getClientTableConfig, MarketOption } from "@/config/tableConfigs/clientTableConfig";
import { clientService } from "@/services/clientService";
import { ClientType } from "@/types/db/clients";
import useAuth from "@/hooks/useAuth";
import { FromDB } from "@/types";
import { NoPermissionScreen } from "@/components/screens/shared/NoPermissionScreen";
import { DeleteClientModal } from "@/components/Modals/DeleteClientModal";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { supabase } from "@/lib/supabase-client";
import { useSetPageTitle } from "@/hooks/useSetPageTitle";

export default function ClientsPage() {
  const { userRoles, displayNames } = useAuth();
  const [marketOptions, setMarketOptions] = useState<MarketOption[]>([]);
  useSetPageTitle(displayNames?.clients?.plural);

  // Fetch markets on mount
  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .schema("lists")
        .from("markets")
        .select("id, name")
        .order("name", { ascending: true });

      if (data) {
        setMarketOptions(data.map((m) => ({ value: m.id, label: m.name })));
      }
    })();
  }, []);

  const tableConfig = useMemo(
    () => getClientTableConfig(userRoles, marketOptions, displayNames),
    [userRoles, marketOptions, displayNames],
  );

  // Check if user has clients read permission
  const hasClientsAccess = useMemo(() => {
    return userRoles?.some((r) => r.permissions?.clients?.read === true) ?? false;
  }, [userRoles]);

  const deleteConfirmation: DeleteConfirmationConfig<FromDB<ClientType>> = useMemo(
    () => ({
      storageKey: "client_delete_skip_confirmation",
      renderModal: ({ count, onConfirm, onCancel }) => (
        <DeleteClientModal
          count={count}
          onConfirm={async (dontShowAgain) => {
            if (dontShowAgain) {
              await AsyncStorage.setItem("client_delete_skip_confirmation", "true");
            }
            onConfirm();
          }}
          onCancel={onCancel}
        />
      ),
    }),
    [],
  );

  if (!hasClientsAccess) {
    return <NoPermissionScreen entityName={displayNames?.clients?.plural || "Clients"} />;
  }

  return (
    <EntityTablePage<FromDB<ClientType>>
      {...tableConfig}
      fetchData={clientService.getClients}
      updateEntities={clientService.saveRowPatches}
      deleteEntities={clientService.deleteClientsByIds}
      deleteConfirmation={deleteConfirmation}
    />
  );
}
